<template>
  <v-col class="project-sync-view">
    <!-- Header -->
    <v-row>
      <v-col>
        <v-card class="elevation-0">
          <v-card-title class="text-h5">
            <font-awesome-icon
              :icon="['fas', 'sync-alt']"
              class="mr-3"
            />
            Project Sync: Google Sheets Comparison
          </v-card-title>

          <v-card-subtitle class="pt-2">
            Compare projects from a Google Sheet with projects in our system.
            This tool performs exact matching on projectIds and projectCodes to identify discrepancies between the system of records and the spreadsheet.
            Select projects from the "In System" tab to add them to your tracking spreadsheet.
          </v-card-subtitle>

          <v-card-actions class="px-6 pb-6">
            <v-btn
              class="mr-4"
              color="primary"
              :loading="loading"
              :disabled="loading || !hasSheetAccess"
              @click="runComparison"
            >
              <font-awesome-icon
                :icon="['fas', 'play']"
                class="mr-2"
              />
              Run Comparison
            </v-btn>

            <v-btn
              v-if="comparisonResults"
              class="mr-4"
              outlined
              :disabled="loading"
              @click="exportResults"
            >
              <font-awesome-icon
                :icon="['fas', 'download']"
                class="mr-2"
              />
              Export Results
            </v-btn>

            <v-spacer />

            <!-- Compact permission status when granted -->
            <div
              v-if="hasSheetAccess"
              class="d-flex align-center mr-3"
            >
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                class="success--text mr-2"
              />
              <span class="text-body-2 success--text">
                Permissions Granted
              </span>
            </div>

            <v-chip
              v-if="!hasSheetAccess"
              color="warning"
              text-color="white"
              small
            >
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                class="mr-2"
              />
              Additional permissions required
            </v-chip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- @adops.com Email Restriction Alert -->
    <v-row v-if="!isProjectSyncUser">
      <v-col>
        <v-alert
          type="info"
          prominent
          border="left"
          colored-border
          color="blue-grey"
        >
          <v-row align="center">
            <v-col class="grow">
              <div class="text-h6">
                <font-awesome-icon
                  :icon="['fas', 'lock']"
                  class="mr-2"
                />
                Restricted Access
              </div>
              <div class="mt-2">
                This feature is currently restricted to internal team members with <strong>@adops.com</strong> email addresses.
                Google Sheets integration requires elevated permissions that are only available to internal users.
              </div>
              <div class="mt-2 caption grey--text">
                Your current email: <strong>{{ currentUser?.docId || 'Not available' }}</strong>
              </div>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Permission Warning - Only show when access NOT granted -->
    <v-row v-if="!loading && isProjectSyncUser && !hasSheetAccess">
      <v-col>
        <v-alert
          type="warning"
          prominent
          border="left"
        >
          <v-row align="center">
            <v-col class="grow">
              <div class="text-h6">
                <font-awesome-icon
                  :icon="['fas', 'exclamation-triangle']"
                  class="mr-2"
                />
                Google Sheets Access Required
              </div>

              <div class="mt-2">
                <strong>Additional permissions are required</strong> to access the project spreadsheet.
                Click "Grant Access" to re-authenticate and grant these permissions.
              </div>
            </v-col>

            <v-col class="shrink">
              <v-btn
                color="primary"
                @click="requestSheetAccess"
              >
                <font-awesome-icon
                  :icon="['fas', 'key']"
                  class="mr-2"
                />
                Grant Access
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Error Message -->
    <v-row v-if="error">
      <v-col>
        <v-alert
          type="error"
          outlined
          dismissible
          @input="error = null"
        >
          {{ error }}

          <!-- Show cache clearing option for COOP/OAuth errors -->
          <v-row
            v-if="error && (error.includes('permission') || error.includes('access') || error.includes('authenticate'))"
            class="mt-3"
          >
            <v-col>
              <v-divider class="mb-3" />
              <div class="text-subtitle-2 mb-2">
                <font-awesome-icon
                  :icon="['fas', 'info-circle']"
                  class="mr-1"
                />
                Troubleshooting: If the issue persists, try clearing your browser cache.
              </div>

              <v-btn
                color="primary"
                small
                outlined
                @click="showCacheClearDialog = true"
              >
                <font-awesome-icon
                  :icon="['fas', 'broom']"
                  class="mr-2"
                />
                Clear Cache & Retry
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Summary Cards -->
    <v-row v-if="comparisonResults">
      <v-col
        cols="12"
        md="3"
      >
        <v-card class="text-center">
          <v-card-text>
            <div class="text-h4 success--text">
              {{ comparisonResults.summary.totalProjectsInSystem || 0 }}
            </div>
            <div class="text-subtitle-1 text--secondary">
              Total in System
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="3"
      >
        <v-card class="text-center">
          <v-card-text>
            <div class="text-h4 success--text">
              {{ comparisonResults.summary.totalProjectsInSheet || 0 }}
            </div>
            <div class="text-subtitle-1 text--secondary">
              Total in Sheet
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        md="3"
      >
        <v-card class="text-center">
          <v-card-text>
            <div class="text-h4 success--text">
              {{ comparisonResults.summary.totalProjectsMatched || 0 }}
            </div>
            <div class="text-subtitle-1 text--secondary">
              Total Matched
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabs for Different Views -->
    <v-row v-if="comparisonResults">
      <v-col>
        <v-card>
          <v-tabs v-model="activeTab">
            <v-tab>
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                class="mr-2"
              />
              Matched ({{ comparisonResults.summary.totalProjectsInSheetAndSystem }})
            </v-tab>

            <v-tab>
              <font-awesome-icon
                :icon="['fas', 'table']"
                class="mr-2"
              />
              In Sheet ({{ comparisonResults.summary.totalProjectsInSheet }})
            </v-tab>

            <v-tab>
              <font-awesome-icon
                :icon="['fas', 'database']"
                class="mr-2"
              />
              In System ({{ comparisonResults.summary.totalProjectsInSystem }})
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- Matched Projects Tab -->
            <v-tab-item>
              <v-card-text>
                <v-row class="mb-2">
                  <v-col>
                    <v-text-field
                      v-model="searchMatched"
                      prepend-inner-icon="search"
                      label="Search matched projects..."
                      clearable
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="auto">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          outlined
                          v-bind="attrs"
                          v-on="on"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'columns']"
                            class="mr-2"
                          />
                          Columns
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item
                          v-for="header in matchedHeaders"
                          :key="header.value"
                          @click="toggleColumnVisibility('matched', header.value)"
                        >
                          <v-list-item-action>
                            <Checkbox
                              :input-value="isColumnVisible('matched', header.value)"
                              @click.stop="toggleColumnVisibility('matched', header.value)"
                            />
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ header.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="visibleMatchedHeaders"
                  :items="comparisonResults.projectsInSheetAndSystem"
                  :search="searchMatched"
                  :items-per-page="10"
                  class="elevation-1"
                >
                  <template v-slot:[`item.matchScore`]="{ item }">
                    <v-chip
                      :color="getMatchScoreColor(item.matchScore)"
                      small
                      text-color="white"
                    >
                      {{ item.matchScore }}%
                    </v-chip>
                  </template>

                  <template v-slot:[`item.differences`]="{ item }">
                    <v-chip
                      v-if="item.differences.length === 0"
                      color="success"
                      small
                      text-color="white"
                    >
                      None
                    </v-chip>

                    <v-chip
                      v-else
                      color="warning"
                      small
                      text-color="white"
                    >
                      {{ item.summary.totalProjectsWithDifferences }} differences
                    </v-chip>
                  </template>

                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      icon
                      small
                      @click="showDifferences(item)"
                    >
                      <font-awesome-icon :icon="['fas', 'eye']" />
                    </v-btn>
                  </template>

                  <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length">
                      <v-card
                        flat
                        class="my-2"
                      >
                        <v-card-text>
                          <div class="text-h6 mb-2">
                            Field Differences
                          </div>

                          <v-simple-table v-if="item.differences.length > 0">
                            <template v-slot:default>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Spreadsheet Value</th>
                                  <th>System Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr
                                  v-for="(diff, index) in item.differences"
                                  :key="index"
                                >
                                  <td><strong>{{ diff.field }}</strong></td>
                                  <td>{{ diff.sheetValue }}</td>
                                  <td>{{ diff.systemValue }}</td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>

                          <div
                            v-else
                            class="text-subtitle-2 success--text"
                          >
                            All fields match between spreadsheet and system.
                          </div>
                        </v-card-text>
                      </v-card>
                    </td>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>

            <!-- Only in Sheet Tab -->
            <v-tab-item>
              <v-card-text>
                <v-row class="mb-2">
                  <v-col>
                    <v-text-field
                      v-model="searchSheetOnly"
                      prepend-inner-icon="search"
                      label="Search spreadsheet projects..."
                      clearable
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="auto">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          outlined
                          v-bind="attrs"
                          v-on="on"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'columns']"
                            class="mr-2"
                          />
                          Columns
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item
                          v-for="header in sheetHeaders"
                          :key="header.value"
                          @click="toggleColumnVisibility('sheetOnly', header.value)"
                        >
                          <v-list-item-action>
                            <Checkbox
                              :input-value="isColumnVisible('sheetOnly', header.value)"
                              @click.stop="toggleColumnVisibility('sheetOnly', header.value)"
                            />
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ header.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="visibleSheetOnlyHeaders"
                  :items="comparisonResults.projectsInSheetOnly"
                  :search="searchSheetOnly"
                  :items-per-page="10"
                  class="elevation-1"
                >
                  <template v-slot:[`item.suggestedProjectCode`]="{ item }">
                    <v-chip
                      v-if="item.suggestedProjectCode"
                      small
                      color="info"
                      text-color="white"
                    >
                      {{ item.suggestedProjectCode }}
                    </v-chip>
                    <span
                      v-else
                      class="text--secondary"
                    >
                      Not found
                    </span>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>

            <!-- In System Tab -->
            <v-tab-item>
              <v-card-text>
                <v-alert
                  v-if="selectedSystemProjects.length > 0"
                  type="info"
                  dense
                  class="mb-4"
                >
                  <v-row align="center">
                    <v-col>
                      {{ selectedSystemProjects.length }} project{{ selectedSystemProjects.length > 1 ? 's' : '' }} selected
                    </v-col>
                    <v-col class="shrink">
                      <v-btn
                        color="success"
                        small
                        :loading="writingBack"
                        :disabled="writingBack || !hasSheetAccess"
                        @click="writeBackSelectedProjects"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'upload']"
                          class="mr-2"
                        />
                        Write Back to Sheet
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-alert>

                <v-row class="mb-2">
                  <v-col>
                    <v-text-field
                      v-model="searchSystemOnly"
                      prepend-inner-icon="search"
                      label="Search system projects..."
                      clearable
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="auto">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          outlined
                          v-bind="attrs"
                          v-on="on"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'columns']"
                            class="mr-2"
                          />
                          Columns
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item
                          v-for="header in systemHeaders"
                          :key="header.value"
                          @click="toggleColumnVisibility('systemOnly', header.value)"
                        >
                          <v-list-item-action>
                            <Checkbox
                              :input-value="isColumnVisible('systemOnly', header.value)"
                              @click.stop="toggleColumnVisibility('systemOnly', header.value)"
                            />
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ header.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-col>
                </v-row>

                <v-data-table
                  v-model="selectedSystemProjects"
                  :headers="visibleSystemOnlyHeaders"
                  :items="comparisonResults.projectsInSystemOnly"
                  :search="searchSystemOnly"
                  :items-per-page="10"
                  show-select
                  item-key="projectId"
                  class="elevation-1"
                >
                  <template v-slot:[`item.projectCode`]="{ item }">
                    <v-chip
                      small
                      color="primary"
                      outlined
                    >
                      {{ item.projectCode }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>

    <!-- Differences Dialog -->
    <v-dialog
      v-model="differencesDialog"
      max-width="800"
    >
      <v-card v-if="selectedMatch">
        <v-card-title>
          Project Comparison Details
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col>
              <div class="text-h6">
                Spreadsheet Data
              </div>
              <div><strong>Client:</strong> {{ selectedMatch.projectInSheet.clientName }}</div>
              <div><strong>Type:</strong> {{ selectedMatch.projectInSheet.projectType }}</div>
              <div><strong>Status:</strong> {{ selectedMatch.projectInSheet.projectStatus }}</div>
            </v-col>

            <v-col>
              <div class="text-h6">
                System Data
              </div>
              <div><strong>Project:</strong> {{ selectedMatch.projectInSystem.projectName }}</div>
              <div><strong>Code:</strong> {{ selectedMatch.projectInSystem.projectCode }}</div>
              <div><strong>ID:</strong> {{ selectedMatch.projectInSystem.projectId }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <div class="text-h6 mb-2">
            Field Differences ({{ selectedMatch.differences.length }})
          </div>

          <v-simple-table v-if="selectedMatch.differences.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Spreadsheet</th>
                  <th>System</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(diff, index) in selectedMatch.differences"
                  :key="index"
                >
                  <td><strong>{{ diff.field }}</strong></td>
                  <td>{{ diff.sheetValue || '(empty)' }}</td>
                  <td>{{ diff.systemValue || '(empty)' }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div
            v-else
            class="text-subtitle-2 success--text"
          >
            All compared fields match.
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="differencesDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cache Clear Dialog -->
    <v-dialog
      v-model="showCacheClearDialog"
      max-width="600"
    >
      <v-card>
        <v-card-title class="text-h6">
          <font-awesome-icon
            :icon="['fas', 'broom']"
            class="mr-2"
          />
          Clear Browser Cache
        </v-card-title>

        <v-card-text>
          <v-alert
            type="info"
            dense
            outlined
            class="mb-4"
          >
            <div class="text-subtitle-2 mb-2">
              <strong>Why clear cache?</strong>
            </div>
            <div class="text-body-2">
              Authentication issues can sometimes be caused by cached security headers.
              Clearing your browser cache will ensure you get the latest configuration.
            </div>
          </v-alert>

          <div class="text-body-1 mb-3">
            <strong>Option 1: Automatic Cache Clear</strong> (Recommended)
          </div>
          <div class="text-body-2 mb-3">
            Click the button below to clear all application caches and reload the page.
          </div>
          <v-btn
            color="primary"
            :loading="clearingCache"
            block
            @click="clearCachesAndReload"
          >
            <font-awesome-icon
              :icon="['fas', 'sync-alt']"
              class="mr-2"
            />
            Clear Cache & Reload
          </v-btn>

          <v-divider class="my-4" />

          <div class="text-body-1 mb-3">
            <strong>Option 2: Manual Hard Refresh</strong>
          </div>
          <div class="text-body-2 mb-2">
            You can also perform a hard refresh using your keyboard:
          </div>
          <v-list dense>
            <v-list-item>
              <v-list-item-icon>
                <font-awesome-icon :icon="['fab', 'apple']" />
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Mac</v-list-item-title>
                <v-list-item-subtitle>
                  <kbd>Cmd</kbd> + <kbd>Shift</kbd> + <kbd>R</kbd>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-icon>
                <font-awesome-icon :icon="['fab', 'windows']" />
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Windows</v-list-item-title>
                <v-list-item-subtitle>
                  <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>F5</kbd>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showCacheClearDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import Checkbox from '@/tools/components/Checkbox.vue'

const { mapActions: mapAdminActions } = createNamespacedHelpers('admin')
const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'AdminProjectSync',

  components: {
    Checkbox,
  },

  data() {
    return {
      loading: false,
      writingBack: false,
      error: null,
      comparisonResults: null,
      hasSheetAccess: false,
      activeTab: 0,

      searchMatched: '',
      searchSheetOnly: '',
      searchSystemOnly: '',

      // Used to track projects to update in the sheet.
      selectedSystemProjects: [],

      differencesDialog: false,
      selectedMatch: null,

      showCacheClearDialog: false,
      clearingCache: false,

      // Column visibility tracking
      columnVisibility: {
        matched: {},
        sheetOnly: {},
        systemOnly: {},
      },

      matchedHeaders: [
        {
          text: 'System Project',
          value: 'projectInSystem.projectName',
          sortable: true,
          visible: true,
        },
        {
          text: 'Sheet Client',
          value: 'projectInSheet.clientName',
          sortable: true,
          visible: true,
        },
        {
          text: 'System ID',
          value: 'projectInSystem.projectId',
          sortable: true,
          visible: true,
        },
        {
          text: 'Sheet ID',
          value: 'projectInSheet.projectId',
          sortable: true,
          visible: true,
        },
        {
          text: 'System Code',
          value: 'projectInSystem.projectCode',
          sortable: true,
          visible: true,
        },
        {
          text: 'Sheet Code',
          value: 'projectInSheet.projectCode',
          sortable: true,
          visible: true,
        },
        {
          text: 'System Type',
          value: 'projectInSystem.projectType',
          sortable: true,
          visible: false,
        },
        {
          text: 'Sheet Type',
          value: 'projectInSheet.projectType',
          sortable: true,
          visible: false,
        },
        {
          text: 'Differences',
          value: 'projectInSheet.differenceTotal',
          sortable: false,
          align: 'center',
          visible: false,
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          visible: true,
        },
      ],

      sheetHeaders: [
        {
          text: 'Client Name',
          value: 'projectInSheet.clientName',
          sortable: true,
          visible: true,
        },
        {
          text: 'ID',
          value: 'projectInSheet.projectId',
          sortable: true,
          visible: true,
        },
        {
          text: 'Code',
          value: 'projectInSheet.projectCode',
          sortable: true,
          visible: true,
        },
        {
          text: 'Type',
          value: 'projectInSheet.projectType',
          sortable: true,
          visible: true,
        },
        {
          text: 'Status',
          value: 'projectInSheet.projectStatus',
          sortable: true,
          visible: true,
        },
        {
          text: 'In System',
          value: 'isInSystem',
          sortable: true,
          visible: true,
        },
        {
          text: 'Suggested ID',
          value: 'projectInSheet.suggestedProjectId',
          sortable: true,
          visible: true,
        },
        {
          text: 'Suggested Project',
          value: 'projectInSheet.suggestedProjectCode',
          sortable: true,
          visible: true,
        },
      ],

      // System Only Projects Table Headers
      // System projects are simple objects native to our system, so we can just reference the fields directly.
      // In contrast, sheet projects are nested because they come from an external data source and require associations to system projects. As the objects in the system of record, system projects do not need a reference back to sheet projects.
      systemHeaders: [
        {
          text: 'ID',
          value: 'projectId',
          sortable: true,
          visible: true,
        },
        {
          text: 'Name',
          value: 'projectName',
          sortable: true,
          visible: true,
        },
        {
          text: 'Code',
          value: 'projectCode',
          sortable: true,
          visible: true,
        },
        {
          text: 'Type',
          value: 'projectType',
          sortable: true,
          visible: true,
        },
        {
          text: 'Status',
          value: 'projectStatus',
          sortable: true,
          visible: true,
        },
        {
          text: 'In Sheet',
          value: 'isInSheet',
          sortable: true,
          visible: true,
        },
        {
          text: 'Client',
          value: 'suggestedClientName',
          sortable: true,
          visible: true,
        },
      ],
    }
  },

  computed: {
    ...mapAuthGetters([
      'currentUser',
      'hasAccessToProjectManagement',
    ]),

    isProjectSyncUser() {
      // This asserts that the user has the custom admin role,
      // has access to projects, and has an internal role.
      return this.hasAccessToProjectManagement
    },

    /**
     * Filters matched headers based on visibility settings
     */
    visibleMatchedHeaders() {
      return this.matchedHeaders.filter((header) => this.isColumnVisible('matched', header.value))
    },

    /**
     * Filters sheet-only headers based on visibility settings
     */
    visibleSheetOnlyHeaders() {
      return this.sheetHeaders.filter((header) => this.isColumnVisible('sheetOnly', header.value))
    },

    /**
     * Filters system-only headers based on visibility settings
     */
    visibleSystemOnlyHeaders() {
      return this.systemHeaders.filter((header) => this.isColumnVisible('systemOnly', header.value))
    },
  },

  async created() {
    // Check if user has sheet access by checking their auth scopes
    await this.checkSheetAccess()

    // Initialize column visibility from defaults
    this.initializeColumnVisibility()
  },

  methods: {
    ...mapAdminActions(['compareProjectData', 'fetchSpreadsheetData']),

    /**
     * Initialize column visibility from header defaults
     */
    initializeColumnVisibility() {
      // Initialize matched headers
      this.matchedHeaders.forEach((header) => {
        this.$set(this.columnVisibility.matched, header.value, header.visible !== false)
      })

      // Initialize sheetOnly headers
      this.sheetHeaders.forEach((header) => {
        this.$set(this.columnVisibility.sheetOnly, header.value, header.visible !== false)
      })

      // Initialize systemOnly headers
      this.systemHeaders.forEach((header) => {
        this.$set(this.columnVisibility.systemOnly, header.value, header.visible !== false)
      })
    },

    /**
     * Check if a column is visible
     * @param {string} table - Table name (matched, sheetOnly, systemOnly)
     * @param {string} value - Column value identifier
     * @returns {boolean} Whether the column is visible
     */
    isColumnVisible(table, value) {
      return this.columnVisibility[table][value] !== false
    },

    /**
     * Toggle column visibility
     * @param {string} table - Table name (matched, sheetOnly, systemOnly)
     * @param {string} value - Column value identifier
     */
    toggleColumnVisibility(table, value) {
      const currentValue = this.columnVisibility[table][value]
      this.$set(this.columnVisibility[table], value, !currentValue)
    },

    async checkSheetAccess() {
      try {
        const hasPermissions = this.hasAccessToProjectManagement

        // Check if user has a valid Google Sheets access token
        this.hasSheetAccess = hasPermissions
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking sheet access:', error)

        this.hasSheetAccess = false
      }
    },

    async requestSheetAccess() {
      try {
        this.error = null
        this.loading = true

        // Use the googleAuth utility to request fresh token with Sheets scopes
        const { requestGoogleSheetsAccess } = await import('@/utils/googleAuth')

        // This will try popup first, then redirect if popup fails
        const token = await requestGoogleSheetsAccess()

        if (token) {
          // Popup succeeded - refresh access check
          await this.checkSheetAccess()

          if (this.hasSheetAccess) {
            // Show success message
            this.$store.commit('tools/snack', {
              message: 'Google Sheets access granted successfully',
              status: 'success',
            }, { root: true })
          } else {
            this.error = 'Token obtained but access check failed. Please refresh the page and try again.'
          }
        } else {
          // Redirect was triggered - user will be redirected away
          // Don't show error, the page will reload after redirect
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error requesting sheet access:', error)

        // Handle specific error cases
        if (error.code === 'auth/popup-closed-by-user') {
          this.error = 'Authentication cancelled. Please try again when ready.'
        } else if (error.code === 'auth/popup-blocked') {
          this.error = 'Popup was blocked by your browser. Please allow popups for this site and try again.'
        } else {
          this.error = error.message || 'Failed to request additional permissions. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    async runComparison() {
      try {
        this.error = null
        this.loading = true

        // Run comparison - token management is handled internally
        const results = await this.$store.dispatch('admin/compareProjectData')

        this.comparisonResults = results
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error running comparison:', error)

        // Handle specific error cases
        if (error.message && error.message.includes('access token')) {
          this.error = 'Google Sheets access required. Please click "Grant Access" to authenticate.'
          this.hasSheetAccess = false
        } else if (error.message && error.message.includes('VUE_APP_PROJECT_SYNC_SHEET_ID')) {
          this.error = 'Project Sync configuration is missing. Please contact your administrator.'
        } else {
          this.error = error.message || 'Failed to compare projects. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    showDifferences(matchedItem) {
      this.selectedMatch = matchedItem
      this.differencesDialog = true
    },

    getMatchScoreColor(score) {
      if (score >= 90) return 'success'
      if (score >= 70) return 'warning'
      return 'error'
    },

    exportResults() {
      if (!this.comparisonResults) return

      // Create CSV content
      let csv = 'Category,Client Name,Project Type,Status,Match Score,System Project Name,System Project Code\n'

      // Add matched projects
      this.comparisonResults.projectsInSheetAndSystem.forEach((match) => {
        csv += `Matched,"${match.projectInSheet.clientName}","${match.projectInSheet.projectType}","${match.projectInSheet.projectStatus}",${match.matchScore}%,"${match.projectInSystem.projectName}","${match.projectInSystem.projectCode}"\n`
      })

      // Add sheet-only projects
      this.comparisonResults.projectsInSheetOnly.forEach((item) => {
        csv += `Sheet Only,"${item.projectInSheet.clientName}","${item.projectInSheet.projectType}","${item.projectInSheet.projectStatus}",N/A,N/A,N/A\n`
      })

      // Add system-only projects
      this.comparisonResults.projectsInSystemOnly.forEach((project) => {
        csv += `System Only,"${project.clientName || 'N/A'}","${project.projectType || 'N/A'}","${project.projectStatus || 'N/A'}",N/A,"${project.projectName}","${project.projectCode}"\n`
      })

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `project-sync-results-${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      window.URL.revokeObjectURL(url)
    },

    async writeBackToSheet() {
      if (!this.comparisonResults || !this.comparisonResults.projectsInSheetAndSystem.length) {
        return
      }

      try {
        this.error = null
        this.writingBack = true

        // Write matched project codes back to the spreadsheet
        const result = await this.$store.dispatch('admin/writeProjectCodesToSheet', {
          projectsInSheetAndSystem: this.comparisonResults.projectsInSheetAndSystem,
          startRow: 2, // Assuming row 1 is headers
        })

        // Show success message
        this.$store.commit('tools/snack', {
          message: result.message || `Successfully updated ${result.updatedRows} rows`,
          status: 'success',
        }, { root: true })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error writing back to sheet:', error)

        this.error = error.message || 'Failed to write project codes to spreadsheet. Please try again.'

        this.$store.commit('tools/snack', {
          message: this.error,
          status: 'error',
        }, { root: true })
      } finally {
        this.writingBack = false
      }
    },

    async writeBackSelectedProjects() {
      if (!this.selectedSystemProjects || this.selectedSystemProjects.length === 0) {
        this.$store.commit('tools/snack', {
          message: 'Please select at least one project to write back',
          status: 'warning',
        }, { root: true })
        return
      }

      try {
        this.error = null
        this.writingBack = true

        // Write selected system projects to spreadsheet
        const result = await this.$store.dispatch('admin/writeSystemProjectsToSheet', {
          projects: this.selectedSystemProjects,
        })

        // Show success message
        this.$store.commit('tools/snack', {
          message: result.message || `Successfully added ${result.addedRows} project${result.addedRows > 1 ? 's' : ''} to spreadsheet`,
          status: 'success',
        }, { root: true })

        // Clear selection after successful write
        this.selectedSystemProjects = []

        // Optionally, re-run comparison to update the view
        if (result.success) {
          await this.runComparison()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error writing back to sheet:', error)

        this.error = error.message || 'Failed to write projects to spreadsheet. Please try again.'

        this.$store.commit('tools/snack', {
          message: this.error,
          status: 'error',
        }, { root: true })
      } finally {
        this.writingBack = false
      }
    },

    async clearCachesAndReload() {
      try {
        this.clearingCache = true

        // Import and use the cache clearing utility
        const { forceServiceWorkerUpdate } = await import('@/utils/service-worker')

        // This will clear all caches and reload the page
        await forceServiceWorkerUpdate()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error clearing caches:', error)

        this.clearingCache = false
        this.showCacheClearDialog = false

        // Fallback: Just reload the page with cache bypass
        this.error = 'Could not clear caches automatically. Please try a hard refresh (Cmd+Shift+R or Ctrl+Shift+F5).'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.project-sync-view {
  .v-card {
    border-radius: 8px;
  }

  .v-data-table {
    ::v-deep tbody tr:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  .text-h4 {
    font-weight: 600;
  }
}
</style>
